import React, { useCallback, useEffect } from 'react'
import { ToastOptions, toast } from 'react-toastify'

import { CloseIcon } from '@nx/fire/assets'
import { getTranslate } from '@nx/translations'
import { useUserInteraction } from '@nx/utils/google-tag-manager'

import {
  StyledCloseButton,
  StyledContent,
  StyledIncreaseBid,
  StyledLabel,
  StyledMessage,
  StyledToastContainer,
} from './GenericOutbidToast.styles'
import translate from './GenericOutbidToast.translate.json'

const { text } = getTranslate(translate)

interface ShowGenericOutbidToastProps extends GenericOutbidToastProps {
  toastOptions?: ToastOptions
}

interface GenericOutbidToastProps {
  auctionId: string
  lotId: string
  onIncreaseBidClick: () => void
}

export const GenericOutbidToast = ({
  auctionId,
  lotId,
  onIncreaseBidClick,
}: GenericOutbidToastProps) => {
  const { recordInteraction } = useUserInteraction()

  const onCTAClick = useCallback(() => {
    recordInteraction({
      action: 'increaseBid.click',
      category: 'Outbid Notification - Global Toast',
      label: `${auctionId}-${lotId}`,
    })

    onIncreaseBidClick()

    toast.dismiss(`${auctionId}-${lotId}`)
  }, [auctionId, lotId, onIncreaseBidClick, recordInteraction])

  useEffect(() => {
    recordInteraction({
      action: 'outbidNotif.display',
      category: 'Outbid Notification - Global Toast',
      label: `${auctionId}-${lotId}`,
    })
  }, [])

  return (
    <StyledToastContainer>
      <StyledContent>
        <StyledLabel $level="XS">
          {text('genericOutbidToast.heading')}
        </StyledLabel>
        <StyledMessage>{text('genericOutbidToast.message')}</StyledMessage>
        <StyledIncreaseBid variant="label" onClick={onCTAClick}>
          {text('genericOutbidToast.cta')}
        </StyledIncreaseBid>
      </StyledContent>
    </StyledToastContainer>
  )
}

export const showGenericOutbidToast = ({
  auctionId,
  lotId,
  onIncreaseBidClick,
  toastOptions,
}: ShowGenericOutbidToastProps) => {
  toast(
    <GenericOutbidToast
      lotId={lotId}
      auctionId={auctionId}
      onIncreaseBidClick={onIncreaseBidClick}
    />,
    {
      autoClose: false,
      closeButton: ({ closeToast }) => (
        <StyledCloseButton onClick={closeToast}>
          <CloseIcon title="Close" />
        </StyledCloseButton>
      ),
      className: 'notification-width-auto-border-radius',
      toastId: `${auctionId}-${lotId}`,
      ...toastOptions,
    }
  )
}
